import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getMyBusiness, updateBusiness, uploadLogo, getStaff, createStaffApi, deleteStaffApi, resetStaffPassword, setStaffStatus, mediaUrl } from "@/api";
import { useAuth } from "@/AuthContext";
import { PlatformLayout, EnvBadge, Loading } from "./Layout";
import { ChangePasswordButton } from "./ChangePasswordButton";
import { BrandScope } from "@/theme";
import { LoyaltyBrandingFields, splitConfig } from "./LoyaltyBrandingFields";
import { ShopCockpit } from "./StaffView";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Store, Users, Palette, QrCode, Upload, Loader2, Save, Trash2, Plus, ExternalLink, Copy, Check,
  ShieldCheck, X, KeyRound, CreditCard, MessageSquare, UtensilsCrossed,
} from "lucide-react";
import { toast } from "sonner";
import { PhysicalCardsTab } from "./OwnerPhysicalCards";
import { FeedbackTab } from "./FeedbackTab";
import { MenuTab } from "./MenuTab";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return "Etwas ist schiefgelaufen.";
}

const TABS = [
  { key: "kasse", label: "Kasse", icon: Store },
  { key: "marke", label: "Marke & Regeln", icon: Palette },
  { key: "team", label: "Team", icon: Users },
  { key: "qr", label: "QR-Aushang", icon: QrCode },
  { key: "cards", label: "Karten", icon: CreditCard },
  { key: "feedback", label: "Feedback", icon: MessageSquare },
  { key: "menu", label: "Menükarte", icon: UtensilsCrossed },
];

export const OwnerDashboard = ({ supportMode = false }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("kasse");
  const [business, setBusiness] = useState(null);

  const load = useCallback(async () => {
    const { business } = await getMyBusiness();
    setBusiness(business);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const exitSupport = () => { localStorage.removeItem("bs_support_bid"); navigate("/"); };

  if (!business) return <PlatformLayout logout={handleLogout}><Loading /></PlatformLayout>;

  if (!business.active && !supportMode) return (
    <PlatformLayout user={{}} logout={handleLogout} subtitle={business.name} headerExtra={<EnvBadge />}>
      <div data-testid="owner-inactive-notice" className="max-w-lg mx-auto mt-6 bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-8 text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-3"><ShieldCheck className="h-7 w-7 text-amber-600" /></div>
        <h1 className="font-display font-extrabold text-2xl text-[var(--bs-ink)]">Betrieb vorübergehend deaktiviert</h1>
        <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">Dein Betrieb ist derzeit inaktiv. Alle Daten bleiben erhalten. Bitte wende dich an den Bonstelo-Support, um ihn wieder zu aktivieren.</p>
      </div>
    </PlatformLayout>
  );

  return (
    <PlatformLayout user={{}} logout={handleLogout} subtitle={business.name}
      headerExtra={supportMode
        ? <span data-testid="support-mode-badge" className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-extrabold ring-1 bg-violet-100 text-violet-700 ring-violet-300 whitespace-nowrap"><ShieldCheck className="h-3.5 w-3.5" /> Support-Modus</span>
        : <span className="flex items-center gap-2"><EnvBadge /><ChangePasswordButton variant="light" /></span>}>
      {supportMode && (
        <div data-testid="support-mode-banner" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-violet-50 ring-1 ring-violet-200 px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <ShieldCheck className="h-5 w-5 text-violet-600 shrink-0" />
            <p className="text-sm font-semibold text-violet-900 leading-tight">
              Platform Admin · Support-Modus — verwaltet: <span data-testid="support-business-name" className="font-display font-extrabold">{business.name}</span>
            </p>
          </div>
          <button data-testid="exit-support-button" onClick={exitSupport}
            className="flex items-center gap-1.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 text-xs font-bold press whitespace-nowrap">
            <X className="h-4 w-4" /> Support-Modus beenden
          </button>
        </div>
      )}
      <div className="mb-5 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.filter((t) => (t.key !== "feedback" || business.modules?.feedback) && (t.key !== "menu" || business.modules?.menu)).map((t) => (
          <button key={t.key} data-testid={`owner-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold whitespace-nowrap press ${
              tab === t.key ? "bg-[var(--bs-teal)] text-white shadow-sm" : "bg-white text-slate-500 ring-1 ring-slate-200"
            }`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "kasse" && (
        <BrandScope primary={business.primary_color} secondary={business.secondary_color}>
          <div className="bg-slate-50 rounded-3xl ring-1 ring-slate-100 overflow-hidden">
            <ShopCockpit business={business} canReset={true} />
          </div>
        </BrandScope>
      )}
      {tab === "marke" && <BrandingTab business={business} onSaved={load} />}
      {tab === "team" && <TeamTab hasBusiness={!!business.id} />}
      {tab === "qr" && <QrTab business={business} />}
      {tab === "cards" && <PhysicalCardsTab />}
      {tab === "feedback" && business.modules?.feedback && <FeedbackTab business={business} />}
      {tab === "menu" && business.modules?.menu && <MenuTab business={business} />}
    </PlatformLayout>
  );
};

const BrandingTab = ({ business, onSaved }) => {
  const [name, setName] = useState(business.name);
  const [location, setLocation] = useState(business.location || "");
  const [cfg, setCfg] = useState({
    mode: business.loyalty_mode, stamps_per_card: business.stamps_per_card,
    spend_per_stamp: business.spend_per_stamp, carry_remainder: business.carry_remainder,
    points_per_currency: business.points_per_currency, reward_threshold: business.reward_threshold,
    reward_title: business.reward_title, currency: business.currency,
    stamp_earning_unit: business.stamp_earning_unit || "",
    loyalty_action_ttl_seconds: business.loyalty_action_ttl_seconds || 120,
    primary_color: business.primary_color, secondary_color: business.secondary_color,
    theme_preset: business.theme_preset,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logo = mediaUrl(business.logo_url);

  const save = async () => {
    setBusy(true);
    try {
      const { branding, loyalty } = splitConfig(cfg);
      await updateBusiness({ name: name.trim(), location: location.trim(), branding, loyalty });
      toast.success("Änderungen gespeichert ✓");
      onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await uploadLogo(file); toast.success("Logo aktualisiert ✓"); onSaved(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className="max-w-lg mx-auto bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6 space-y-5">
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-2xl bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center overflow-hidden shrink-0">
          {logo ? <img src={logo} alt="Logo" className="h-full w-full object-contain" /> : <Store className="h-8 w-8 text-slate-300" />}
        </div>
        <label data-testid="owner-logo-upload" className="cursor-pointer inline-flex items-center gap-2 rounded-xl bg-slate-100 hover:bg-slate-200 px-4 py-2.5 font-bold text-sm text-slate-700 press">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Logo hochladen
          <input type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={uploading} />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><label className="text-xs font-bold text-slate-500">Name</label>
          <Input data-testid="owner-name-input" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" /></div>
        <div className="space-y-1.5"><label className="text-xs font-bold text-slate-500">Standort</label>
          <Input data-testid="owner-location-input" value={location} onChange={(e) => setLocation(e.target.value)} className="h-11 rounded-xl" /></div>
      </div>
      <LoyaltyBrandingFields value={cfg} onChange={setCfg} idPrefix="owner" />
      <Button data-testid="owner-save-branding" onClick={save} disabled={busy}
        className="w-full py-3.5 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press disabled:opacity-60">
        {busy ? <><Loader2 className="mr-1 h-5 w-5 animate-spin" /> Speichern…</> : <><Save className="mr-1 h-5 w-5" /> Speichern</>}
      </Button>
    </div>
  );
};

const TeamTab = ({ hasBusiness }) => {
  const [staff, setStaff] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => { setStaff(await getStaff()); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createStaffApi({ name: name.trim(), email: email.trim(), password });
      setName(""); setEmail(""); setPassword("");
      toast.success("Mitarbeiter angelegt ✓");
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await deleteStaffApi(id); toast.success("Mitarbeiter entfernt"); load(); }
    catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const resetPw = async (s) => {
    const pw = window.prompt(`Neues Passwort für ${s.name} (min. 6 Zeichen):`);
    if (!pw) return;
    if (pw.length < 6) { toast.error("Passwort zu kurz (min. 6)."); return; }
    try { await resetStaffPassword(s.id, pw); toast.success("Passwort zurückgesetzt ✓"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const toggleStaff = async (s) => {
    try { await setStaffStatus(s.id, !s.active); toast.success(s.active ? "Mitarbeiter deaktiviert" : "Mitarbeiter aktiviert"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-lg mx-auto space-y-4">
      <form onSubmit={create} className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6 space-y-3">
        <h2 className="font-display font-extrabold text-lg text-[var(--bs-ink)]">Mitarbeiter hinzufügen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input data-testid="staff-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="h-11 rounded-xl" required />
          <Input data-testid="staff-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail" className="h-11 rounded-xl" required />
          <Input data-testid="staff-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passwort (min. 6)" className="h-11 rounded-xl" required minLength={6} />
        </div>
        <Button data-testid="staff-create-button" type="submit" disabled={busy || !hasBusiness}
          className="w-full py-3 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press disabled:opacity-60">
          {busy ? <Loader2 className="mr-1 h-5 w-5 animate-spin" /> : <Plus className="mr-1 h-5 w-5" />} Anlegen
        </Button>
      </form>
      <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft divide-y divide-slate-100 overflow-hidden">
        {staff.length === 0 ? (
          <p className="text-center text-slate-400 text-sm p-6">Noch keine Mitarbeiter.</p>
        ) : staff.map((s) => (
          <div key={s.id} data-testid="staff-list-item" className="flex items-center justify-between px-5 py-3.5">
            <div className="min-w-0">
              <p className="font-display font-bold text-slate-900 text-sm flex items-center gap-2">{s.name}
                <span data-testid="staff-status-badge" className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${s.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{s.active !== false ? "Aktiv" : "Inaktiv"}</span>
              </p>
              <p className="text-xs text-slate-400">{s.email}</p>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="staff-toggle-status-button" title={s.active !== false ? "Deaktivieren" : "Aktivieren"} onClick={() => toggleStaff(s)} className={`px-2.5 py-1.5 rounded-lg text-xs font-bold press ${s.active !== false ? "text-amber-700 hover:bg-amber-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{s.active !== false ? "Deaktivieren" : "Aktivieren"}</button>
              <button data-testid="staff-reset-pw-button" title="Passwort zurücksetzen" onClick={() => resetPw(s)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 press"><KeyRound className="h-4 w-4" /></button>
              <button data-testid="staff-delete-button" onClick={() => remove(s.id)} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50 press"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const QrTab = ({ business }) => {
  const [copied, setCopied] = useState(false);
  const joinUrl = `${window.location.origin}/join/${business.slug}`;
  const copy = async () => { try { await navigator.clipboard.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) {} };
  return (
    <div className="max-w-lg mx-auto bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6 space-y-4">
      <h2 className="font-display font-extrabold text-lg text-[var(--bs-ink)]">Kunden-Onboarding</h2>
      <p className="text-sm text-slate-500 leading-relaxed">Drucke den QR-Aushang für deinen Tresen. Kunden scannen ihn und starten sofort ihre Treuekarte — ganz ohne App.</p>
      <div className="flex items-center gap-2 rounded-2xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3">
        <span data-testid="owner-join-url" className="flex-1 text-sm font-mono text-slate-600 truncate">{joinUrl}</span>
        <button data-testid="owner-copy-join-url" onClick={copy} className="text-slate-500 press">{copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</button>
      </div>
      <Button data-testid="owner-open-poster" onClick={() => window.open(`/poster/${business.slug}`, "_blank")}
        className="w-full py-3.5 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press">
        <ExternalLink className="mr-2 h-4 w-4" /> QR-Aushang öffnen (A4)
      </Button>
    </div>
  );
};
